import { AlertTriangle, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { useTheme } from "../context/ThemeContext";
import { useState, useEffect, useCallback } from "react";
import { getAnomalySummary } from "../services/index";
import { useDistrict } from "../utils/DistrictContext";
import { useNavigate } from "react-router-dom";
import { Spin } from "antd";

// Destructure onNavigate from props
export function AnomalyDetectionCard() {
  const { theme, colors } = useTheme();
  const [selectedTimePeriod, setSelectedTimePeriod] = useState("last_30_days");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { selectedDistrict } = useDistrict();
  const navigate = useNavigate();

  const timePeriods = [
    { label: "D", value: "past_day" },
    { label: "W", value: "last_7_days" },
    { label: "1M", value: "last_30_days" },
    { label: "3M", value: "last_90_days" },
    // { label: "6M", value: "last_6_months" },
    { label: "1Y", value: "last_year" },
  ];

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAnomalySummary(selectedTimePeriod);
      setData(result);
    } catch (error) {
      console.error("Failed to load anomalies:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedTimePeriod]);

  useEffect(() => {
    loadData();
  }, [loadData, selectedDistrict]);

  const totalAnomalies = data?.total_anomaly_meters || 0;
  const totalCategories = data?.total_categories || 0;
  const categories = data?.category_breakdown || [];

  const handleCategoryClick = (categoryName) => {
    navigate("/totalflow", {
      state: {
        // <--- MUST be wrapped in "state"
        tab: "anomaly",
        category: categoryName,
        startDate: data?.start_date,
        endDate: data?.end_date,
        timeRange: selectedTimePeriod,
      },
    });
  };
  return (
    <motion.div
      className="rounded-xl p-6 relative overflow-hidden h-full flex flex-col group "
      style={{
        backgroundColor: theme === "white" ? "#ffffff" : colors.glassEffect,
        border: `1px solid ${colors.border}`,
        backdropFilter: "blur(10px)",
      }}
      initial={{ opacity: 0, x: -50 }}
      animate={{ opacity: 1, x: 0 }}
      whileHover={{ x: 10 }}
    >
      <div
        className="absolute top-0 left-0 h-[2px] w-0 group-hover:w-full transition-all duration-500 rounded-full"
        style={{ backgroundImage: colors.gradient }}
      />

      <div className="relative z-10 flex flex-col h-full">
        <div className="flex items-center justify-between mb-6 w-full gap-2 max-[992px]:flex-wrap">
          <div className="flex items-center gap-3">
            <div
              className="p-2 rounded-lg"
              style={{ backgroundImage: colors.gradient }}
            >
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <h3
              className="text-2xl font-bold tracking-tight"
              style={{ color: colors.text }}
            >
              Anomaly Detection
            </h3>
          </div>

          {/* RIGHT SIDE: Segmented Period Selector (Matches image_d6f226) */}
          <div
            className="flex items-center p-1 rounded-lg border bg-opacity-20 cursor-pointer"
             style={{
                backgroundColor: colors.background,
                borderColor: colors.border,
              }}
            onClick={(e) => e.stopPropagation()}
          >
            {timePeriods.map((tp) => {
              const isActive = selectedTimePeriod === tp.value;
              return (
                <button
                  key={tp.value}
                  onClick={() => setSelectedTimePeriod(tp.value)}
                  className="px-3 py-1 rounded-md text-[10px] min-[1921px]:text-[12px] font-black transition-all duration-200 cursor-pointer"
                  style={{
                    backgroundColor: isActive ? colors.primary : "transparent",
                    color: isActive ? "#ffffff" : colors.textSecondary,
                    // Shadow added to active button for depth
                    boxShadow: isActive ? "0 2px 4px rgba(0,0,0,0.3)" : "none",
                  }}
                >
                  {tp.label}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Spin size="large" />
          </div>
        ) : (
          <div className="flex flex-col h-full">
            <div className="mb-4">
              <div className="flex items-baseline gap-2">
                <motion.span
                  key={totalAnomalies}
                  className="text-5xl font-bold"
                  style={{
                    backgroundImage: colors.gradient,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                >
                  {totalAnomalies}
                </motion.span>
                <span
                  className="text-sm"
                  style={{ color: colors.textSecondary }}
                >
                  anomaly meters detected
                </span>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <div
                  className="w-1.5 h-1.5 rounded-full animate-pulse"
                  style={{ backgroundColor: colors.primary }}
                />
                <span
                  className="text-xs"
                  style={{ color: colors.textSecondary }}
                >
                  Across {totalCategories} categories
                </span>
              </div>
            </div>

            <div className="flex-1 space-y-3 mt-4">
              {categories.map((item, index) => {
                const pct =
                  totalAnomalies > 0
                    ? Math.round(
                        (item.anomaly_meter_count / totalAnomalies) * 100,
                      )
                    : 0;
                return (
                  <motion.div
                    key={item.category_name}
                    className="cursor-pointer"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => handleCategoryClick(item.category_name)} // Trigger Navigation
                  >
                    <div className="flex justify-between text-sm mb-1.5">
                      <span
                        className="font-medium"
                        style={{ color: colors.textSecondary }}
                      >
                        {item.category_name}
                      </span>
                      <div className="flex items-center gap-2">
                        <span
                          className="font-bold"
                          style={{ color: colors.text }}
                        >
                          {item.anomaly_meter_count}
                        </span>
                        <span
                          className="text-xs"
                          style={{ color: colors.textSecondary }}
                        >
                          ({pct}%)
                        </span>
                      </div>
                    </div>
                    <div
                      className="h-1.5 w-full rounded-full overflow-hidden"
                      style={{
                        backgroundColor:
                          theme === "white" ? "#e0e0e0" : `${colors.border}40`,
                      }}
                    >
                      <motion.div
                        className="h-full rounded-full"
                        style={{ backgroundImage: colors.gradient }}
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 1, ease: "easeOut" }}
                      />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
